/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Fragment, FC, MouseEvent, useState, ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Popover, Tooltip, Typography } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import Loader from "src/components/atoms/Loader";
import { useFormatMsg } from "src/hooks/useFormatMsg";
import { FileMetadata } from "../../../../requests/files/types";
import SingleFile from "./SingleFile";
import { getMultipleFilesBtnMsg, getPopoverId } from "../utils";

import * as S from "../styles";

export type FileListProps = {
  files: FileMetadata[];
  filesCount: number;
  disabled: boolean;
  areFilesLoading: boolean;
  fetchFilesOnOpen(): void;
  openFile(file: FileMetadata): void;
  downloadFile(file: FileMetadata): void;
  deleteFile(fileId: string | number): void;
};

const FileList: FC<FileListProps> = ({
  files,
  filesCount,
  areFilesLoading,
  disabled = true,
  fetchFilesOnOpen,
  downloadFile,
  openFile,
  deleteFile,
}) => {
  const formatMsg = useFormatMsg();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openMenu = (e: MouseEvent<HTMLButtonElement>): void => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      fetchFilesOnOpen();
      setAnchorEl(e.currentTarget);
    }
  };

  const closeMenu = (): void => {
    setAnchorEl(null);
  };

  const deleteFileWrapper = (fileId: string | number): void => {
    if (filesCount === 1) {
      closeMenu();
    }
    deleteFile(fileId);
  };

  const renderSingleFile = (file: FileMetadata, index: number): ReactNode => (
    <SingleFile
      key={file.id}
      file={file}
      deleteFile={deleteFileWrapper}
      downloadFile={downloadFile}
      openFile={openFile}
      disabled={disabled}
    />
  );

  if (!filesCount) {
    if (areFilesLoading) return <Loader size={20} />;

    return (
      <div css={S.fileListItemEmptyCss}>
        <Typography style={{ whiteSpace: "normal" }}>
          <FormattedMessage id="segments.file_list.no_files" />
        </Typography>
      </div>
    );
  }

  const popoverId = getPopoverId(Boolean(anchorEl));
  const multipleFilesBtnMsg = getMultipleFilesBtnMsg(filesCount, formatMsg);

  return (
    <Fragment>
      <Tooltip title={multipleFilesBtnMsg} placement="top-start">
        <button
          aria-describedby={popoverId}
          type="button"
          css={S.singleFileContainer(!disabled)}
          onClick={openMenu}
        >
          <span css={S.fileNameCss}>{multipleFilesBtnMsg}</span>
          <Search css={S.iconStyleCss} />
        </button>
      </Tooltip>
      <Popover
        id={popoverId}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <div css={S.fileListWrapperCss}>
          {areFilesLoading ? <Loader size={20} /> : files.map(renderSingleFile)}
        </div>
      </Popover>
    </Fragment>
  );
};

export default FileList;
